/**
 * Reine Funktionen/Hook aus dem Angebots-Builder (`pages/builder.tsx`), OHNE JSX und OHNE
 * Closures ueber `BuilderPage`-State — nur nach hier verschoben, damit sie ohne die
 * 3.500-Zeilen-Seite gelesen und getestet werden koennen (Builder-Refactoring Einheit A,
 * Schritt 1, siehe `docs/KONZEPT-BUILDER-REFACTORING.md` § 4.1). Reines Cut&Paste — keine
 * Logik hat sich beim Umzug geaendert.
 */
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { lineGross, type EngineItem } from "@maestro/pricing";
import type { CatalogItem, OfferItem, OfferPriceMode, OfferSection } from "../ui/types";
import { money, ohneSternchen } from "../ui/format";

/** VAT-Saetze fuer die Anzeige (Zyklus-Button in `ItemRow`) und `vatLabel` unten. */
export const VAT_RATES: { bp: number; label: string }[] = [
  { bp: 700, label: "7 %" },
  { bp: 1900, label: "19 %" },
  { bp: 0, label: "0 %" },
];

/**
 * Drag-and-Drop / Auf-Ab-Pfeile (Umsortieren innerhalb einer Sektion): `items` ist EINE
 * gemeinsame Liste ueber alle Sektionen (Menü, Getränke, Equipment, Personal, Sonstiges) und
 * — bei mehrtaegigen Varianten — zusaetzlich ueber Tage gefiltert (`visibleItems`). Der
 * Autosave bildet `sortOrder` als reinen Array-INDEX ueber die GANZE `items`-Liste
 * (`payloadItems`, `sortOrder: i`). Wuerde man beim Umsortieren nur die sichtbare Teilliste
 * neu bauen und den Rest anhaengen, verschoebe sich die Reihenfolge aller anderen Sektionen
 * (und Tage) ungewollt mit.
 *
 * `subsetIds` ist genau die sichtbare Teilmenge (eine Sektion, ggf. zusaetzlich auf den
 * offenen Tag gefiltert — siehe `secItems`/`visibleItems`). Diese Funktion sortiert NUR
 * innerhalb dieser Teilmenge um und setzt das Ergebnis an genau den Indexpositionen zurueck,
 * an denen die Teilmengen-Elemente vorher in `all` standen — alle anderen Elemente bleiben an
 * ihrem Platz. Reine Funktion, keine Seiteneffekte — darum direkt unit-testbar (siehe
 * `apps/web/test/builder-positionen-umsortieren.test.tsx`).
 */
export function reorderWithinSubset<T extends { id: string }>(all: T[], subsetIds: string[], activeId: string, overId: string): T[] {
  const oldIndex = subsetIds.indexOf(activeId);
  const newIndex = subsetIds.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return all;
  const neueTeilReihenfolge = arrayMove(subsetIds, oldIndex, newIndex);
  const subsetSet = new Set(subsetIds);
  const byId = new Map(all.map((it) => [it.id, it]));
  let cursor = 0;
  return all.map((it) => {
    if (!subsetSet.has(it.id)) return it;
    const nextId = neueTeilReihenfolge[cursor]!;
    cursor += 1;
    return byId.get(nextId)!;
  });
}

/** Katalog-Art -> Angebots-Sektion (dedupliziert die frueher 3x inline wiederholte Zuordnung). */
export function kindToSection(kind: CatalogItem["kind"]): OfferSection {
  return kind === "drink" ? "drinks" : kind === "equipment" ? "equipment" : kind === "staff" ? "staff" : kind === "other" ? "other" : "menu";
}

/**
 * Sinnvoller Preismodus-Vorschlag fuer eine NEU angelegte, noch leere Freitext-Position, je nach
 * Sektion. Anlass (08.09.2026, STORIA/Mimmo): eine leere Zeile bekam bisher IMMER "per_person"
 * (Menge x Gaeste), unabhaengig von der Sektion - bei Equipment/Personal/Sonstiges (Tische,
 * Besteck, Transport) ist die eingetragene Menge aber so gut wie immer schon die absolute
 * Stueckzahl, keine Pro-Gast-Groesse. Menue/Getraenke bleiben bei "per_person" (haeufigster
 * Fall, Ausnahmen faengt der ItemRow-Mismatch-Hinweis ab).
 */
export function defaultPriceModeForSection(section: OfferSection): OfferPriceMode {
  return section === "equipment" || section === "staff" || section === "other" ? "per_unit" : "per_person";
}

export function uuid(): string {
  return crypto.randomUUID();
}
/** Zwei-Buchstaben-Kuerzel fuer den Empfaenger-Avatar im Senden-Dialog. */
export function initials(name: string | undefined | null): string {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
// L10/W6: `centsToEuros` kommt jetzt zentral aus ../ui/format (eine Definition statt Kopien).
export function milliToQty(m: number): string {
  return (m / 1000).toString().replace(".", ",");
}
export function qtyToMilli(v: string): number {
  const n = Number.parseFloat(v.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) : 0;
}
export function vatLabel(bp: number): string {
  return VAT_RATES.find((r) => r.bp === bp)?.label ?? `${(bp / 100).toFixed(0)} %`;
}

/** Katalog-Konvention -> Builder-Zeile. */
export function catalogToItem(ci: CatalogItem, dayNo: number | null, sortOrder: number, qtyMilli = 1000): OfferItem {
  const section = kindToSection(ci.kind);
  const priceMode: OfferPriceMode = ci.pricingUnit === "per_person" ? "per_person" : ci.pricingUnit === "flat" ? "flat" : "per_unit";
  const vatRateBp = ci.vatClass === "food" ? 700 : 1900;
  return {
    id: uuid(),
    optionId: "",
    dayNo,
    section,
    courseLabel: null,
    // Markdown-Sternchen aus dem KI-Import nicht ins Angebot tragen (Katalog bleibt unveraendert).
    name: ohneSternchen(ci.name),
    description: ci.description == null ? null : ohneSternchen(ci.description),
    catalogItemId: ci.id,
    // Einzeln aus dem Katalog gezogen — kein Paketbezug. Paketzeilen entstehen ausschliesslich
    // serverseitig ueber `apply-package` (§5.1), nie hier.
    packageId: null,
    // Migration 384: eine frisch aus dem Katalog gezogene Position gehoert noch keiner Wahlgruppe an.
    choiceGroupId: null,
    qtyMilli,
    unit: null,
    unitPriceCents: ci.priceCents,
    catalogPriceCents: ci.priceCents,
    priceMode,
    vatRateBp,
    isOptional: false,
    discountable: true,
    sortOrder,
  };
}
export function toEngineItems(items: OfferItem[]): EngineItem[] {
  return items.map((it) => ({
    id: it.id,
    section: it.section,
    priceMode: it.priceMode,
    qtyMilli: it.qtyMilli,
    unitPriceCents: it.unitPriceCents,
    vatRateBp: it.vatRateBp,
    // `isOptional` fehlte hier bis zum 17.09.2026 — und damit rechnete der BROWSER anders als der
    // SERVER, obwohl beide dieselbe Engine benutzen. Es ist eine Recheneinheit, aber sie bekam
    // zwei verschiedene Eingaben.
    //
    // Gemeldet vom Betreiber: die Gesamtsumme im Builder zeigte 655,40 EUR, gespeichert waren
    // 405,40 EUR (Vorgang Ivan Kokorov, 17.09.2026). Die Differenz von exakt 250,00 EUR war eine
    // als optional gekennzeichnete Position. Die Engine laesst solche Zeilen aus der Summe heraus
    // (H-21: "reine Anzeige-Zeilen, kein Beitrag zu Summe/Rabatt/USt") — ohne das Kennzeichen
    // konnte sie das im Browser nicht wissen.
    //
    // Seine Beobachtung "bis gestern war es richtig" stimmt genau: solange niemand eine Position
    // als optional markiert, sind beide Eingaben gleich. Der Fehler entsteht mit der ersten
    // optionalen Zeile — und faellt dann sofort mit ihrem vollen Betrag auf.
    isOptional: it.isOptional,
    surcharge: it.surcharge === true,
    guestCount: it.guestCount ?? null,
    discountable: it.discountable,
    dayNo: it.dayNo,
    // Paket inkl. Getraenke: die Engine teilt die Kopfzeile nach USt (paketTeile) — wie der Server.
    packageId: it.packageId,
    catalogPriceCents: it.catalogPriceCents,
    drinksShareCents: it.drinksShareCents ?? null,
    // Wahlgang ("oder"): die Engine rechnet die Alternativen mengengenau bzw. mit der guenstigsten (Migration 755).
    choiceGroupId: it.choiceGroupId ?? null,
    // Gang: gehoert zur EINEN Wahlgang-Definition (`wahlgaengeVerzeichnis`) - sonst rechnete der Browser andere Wahlgaenge als der Server.
    courseLabel: it.courseLabel ?? null,
  }));
}

/**
 * Gepufferte Text-Eingabe fuer Zahlenfelder (Menge/Preis): waehrend des Tippens bleibt der ROH
 * getippte Text erhalten, statt bei jedem Tastendruck kanonisch reformatiert zu werden (z. B.
 * ein gerade getipptes Komma/Dezimalpunkt, das der Parser sofort wieder verschluckt). Ohne diesen
 * Puffer sprang der Cursor beim Tippen am Ende des Felds zurueck bzw. das Feld "reagierte nicht"
 * (Bug: das reformatierte Rendering ueberschrieb den gerade getippten Zwischenzustand). Kanonisch
 * neu formatiert wird erst beim Verlassen des Felds (blur) oder wenn sich der Wert VON AUSSEN
 * aendert (z. B. Server-Reload), waehrend das Feld nicht fokussiert ist.
 */
export function useBufferedField(canonical: string, onCommit: (raw: string) => void) {
  const [raw, setRaw] = useState(canonical);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return {
    value: raw,
    onFocus: () => {
      focused.current = true;
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      setRaw(e.target.value);
      onCommit(e.target.value);
    },
    onBlur: () => {
      focused.current = false;
      setRaw(canonical);
    },
  };
}

/**
 * A4 (UX-Polish 24.08.2026) — die ABSOLUTE Stueckzahl einer Zeile, also die Zahl, die zaehlt,
 * wenn jemand einkauft, kocht und eindeckt. `null` bei `flat`: dort ist die Menge nachweislich
 * wirkungslos (`lineGross` liest `qtyMilli` im flat-Zweig gar nicht, `packages/pricing`), eine
 * Stueckzahl waere dort schlicht erfunden.
 *
 * ── DIE RUNDUNGSREGEL, und warum sie nur EINEN der drei Modi trifft ─────────────────────────
 * `per_person`: die Zahl entsteht erst hier — `qtyMilli` ist eine Menge JE GAST („0,33 pro
 *   Gast"), die absolute Stueckzahl ist `qty × Gaeste` und traegt darum fast immer Nachkomma
 *   (0,33 × 35 = 11,55). **Aufgerundet (`Math.ceil`)**, nicht kaufmaennisch: man serviert keine
 *   halbe Pizza, und von den zwei moeglichen Fehlern ist „eine Portion zu viel" der harmlose —
 *   „eine zu wenig" heisst, ein Gast bekommt nichts. Die Rundung ist bewusst NICHT symmetrisch,
 *   weil die beiden Fehlerrichtungen es auch nicht sind.
 * `per_unit`: hier ist `qtyMilli/1000` bereits die absolute Stueckzahl UND zugleich der Faktor,
 *   mit dem abgerechnet wird (`lineGross`: `qtyMilli × unitPriceCents / 1000`). Aufrunden
 *   waere hier keine Serviervorgabe, sondern eine FALSCHE ANGABE ueber die Rechnung: bei 2,5 kg
 *   à 18,00 € stuenden „3×" ueber einem Betrag von 45,00 €. Deshalb gilt die Regel dort
 *   ausdruecklich NICHT — die Zahl wird unveraendert durchgereicht.
 * `flat`: keine Stueckzahl, siehe oben.
 *
 * ── WARUM DAS DEN PREIS NICHT ANFASSEN KANN ─────────────────────────────────────────────────
 * Der Betrag kommt unveraendert aus `lineGross()` (`@maestro/pricing`, server-autoritativ) und
 * rechnet weiter mit dem exakten `qtyMilli`. Diese Zahl hier ist eine MENGENANGABE, kein
 * Rechenweg — deshalb steht sie in der Anzeige auch nie mit `=` oder `×` neben dem Betrag,
 * sondern durch `·` getrennt: eine aufgerundete Stueckzahl darf nicht so aussehen, als ergaebe
 * sie multipliziert den Betrag daneben (bei 11,55 → „12" taete sie das auch nicht).
 */
export function absoluteStueckzahl(item: Pick<OfferItem, "priceMode" | "qtyMilli">, guests: number): number | null {
  switch (item.priceMode) {
    case "per_person":
      return Math.ceil((item.qtyMilli * guests) / 1000);
    case "per_unit":
      return item.qtyMilli / 1000;
    case "flat":
      return null;
  }
}

/*
 * HIER STAND `festeMengeAbweichend` — entfernt am 22.09.2026 auf Antoines Wort.
 *
 * Sie beantwortete: „bewegt sich diese Menge NICHT mit der Gaestezahl, und weicht sie dabei von
 * ihr ab?" Die Antwort wurde neben der Menge gezeigt, zuerst als Schloss (Punkt 75, Befund ⑥),
 * dann in Worten als „35× ≠ 18" (Entwurf 43).
 *
 * Antoine: „Das kann der Betreiber ganz alleine entscheiden, was er da macht. Da braucht es
 * keinen Hinweis. So hatten wir es ja auch besprochen gehabt." — eine feste Menge ist der
 * Normalfall, nicht der Erklaerungsfall. Der Vermerk bleibt hier stehen, damit derselbe Hinweis
 * nicht ein drittes Mal aus demselben Befund heraus neu erfunden wird.
 */

/** Die Stueckzahl als Anzeigetext („12×", „2,5×"). Nachkomma nur, wo es welches gibt. */
export function stueckzahlText(item: Pick<OfferItem, "priceMode" | "qtyMilli">, guests: number): string | null {
  const n = absoluteStueckzahl(item, guests);
  if (n == null) return null;
  return `${Number.isInteger(n) ? String(n) : String(n).replace(".", ",")}×`;
}

/** Zeigt die Zeilenrechnung im BEARBEITUNGS-Formular an — dort steht das Mengenfeld direkt
 *  daneben, und die Stueckzahl uebersetzt, was der getippte Wert im jeweiligen Preismodus
 *  tatsaechlich bedeutet (aus „0,33" wird sichtbar „12×"). Der Betrag muss hier mit, weil er im
 *  Formular-Zweig nirgends sonst steht (anders als in der Leseansicht, siehe `leseZeile`).
 *  A4: `·` statt `=` — die aufgerundete Stueckzahl ist keine Rechnung, s. `absoluteStueckzahl`. */
export function lineTotalCaption(item: OfferItem, guests: number): string {
  const engineItem: EngineItem = {
    id: item.id,
    section: item.section,
    priceMode: item.priceMode,
    qtyMilli: item.qtyMilli,
    unitPriceCents: item.unitPriceCents,
    vatRateBp: item.vatRateBp,
    discountable: item.discountable,
  };
  const total = money(lineGross(engineItem, guests));
  const stueck = stueckzahlText(item, guests);
  /*
   * `flat`: hier steht GAR NICHTS mehr.
   *
   * Antoine, 17.09.2026, zum aufgeklappten Zeilenformular: „Ist das pauschal, unten, wenn man auf
   * bearbeiten geht, nicht überflüssig?" — er hatte recht. Die Zeile lautete
   * „Pauschal (Menge ohne Wirkung) · 250,00 €" und wiederholte damit zweimal, was direkt daneben schon
   * steht: das Wort „pauschal" ist die Beschriftung des Modus-Knopfes, und 250,00 € steht im
   * Preisfeld eine Zeile darüber. Die einzige Angabe darin, die sonst nirgends stand — dass ein
   * Tippen im Mengenfeld folgenlos ist — steht jetzt am Feld selbst: es ist bei `flat`
   * abgeschaltet (`ItemRow.tsx`). Ein graues Feld sagt das deutlicher als ein Satz darunter.
   *
   * Bei den anderen Modi bleibt der Betrag stehen: dort ist er ein Rechenergebnis
   * („1 pro Gast · 12 Gäste · 1.176,00 €") und nicht die Wiederholung eines Eingabefelds.
   */
  return stueck == null ? "" : `${stueck} · ${total}`;
}

/**
 * LESE-Ansicht einer Zeile: Mengenangabe links, Einzelpreis, Betrag rechts in eigener Spalte
 * („50×" | „à 14,90 €" | „745,00 €").
 *
 * ── A4 (UX-Polish 24.08.2026): warum hier nur noch EINE Zahl steht ──────────────────────────
 * Bis hierher baute diese Zeile den ganzen Rechenweg nach: „0,33 pro Gast · 35 Gäste · 12,50 €".
 * Das ist die Herleitung eines Betrags, der zwei Zentimeter weiter rechts ohnehin fertig steht —
 * und die Frage, die beim Durchsehen einer Positionsliste WIRKLICH aufkommt, beantwortet sie
 * nicht: **wie viel wird davon gebraucht?** Diese Antwort steht jetzt da, und nur sie.
 *
 * Der Betrag daneben kommt unveraendert aus `lineGross()` — an der Preislogik aendert A4 nichts,
 * es ist reine Darstellung. Was die Zahl bedeutet und warum sie bei `per_person` aufgerundet ist
 * (und bei `per_unit` ausdruecklich nicht), steht bei `absoluteStueckzahl`.
 *
 * ── Bughunt 27.08.2026 (Antoine, Live-Screenshot): der Einzelpreis kommt zurueck ────────────
 * "50× 745,00 €" liest sich wie eine falsche Rechnung (50×745 ≠ 745) — ohne den Einzelpreis
 * daneben ist nicht zu unterscheiden, ob 745,00 € der Zeilen-GESAMTBETRAG (Menge bereits
 * eingerechnet) oder ein fehlerhafter Einzelpreis ist. `einzelpreis` schliesst genau diese
 * Luecke, OHNE die A4-Vereinfachung rueckgaengig zu machen: es kommt keine Herleitung
 * ("0,33 pro Gast · 35 Gäste") zurueck, nur die eine zusaetzliche Zahl, die die Verwechslung
 * ausschliesst. `item.unit` (die fruehere Sonderbehandlung von "Person"/"Gast") bleibt bewusst
 * draussen — dafuer gilt die A4-Begruendung unveraendert.
 */
export function leseZeile(item: OfferItem, guests: number): { formel: string; betrag: string; einzelpreis: string | null } {
  const engineItem: EngineItem = {
    id: item.id,
    section: item.section,
    priceMode: item.priceMode,
    qtyMilli: item.qtyMilli,
    unitPriceCents: item.unitPriceCents,
    vatRateBp: item.vatRateBp,
    discountable: item.discountable,
  };
  const betrag = money(lineGross(engineItem, guests));
  const stueck = stueckzahlText(item, guests);
  // `flat` hat keine Stueckzahl (Menge wirkungslos) — dort bleibt das Wort stehen, das den
  // fehlenden Faktor erklaert, statt eine „1×" zu erfinden, die niemand gepflegt hat. Aus
  // demselben Grund gibt es dort auch keinen sinnvollen "Einzelpreis": unitPriceCents IST
  // bereits der Zeilenbetrag, ihn nochmal daneben zu schreiben waere die Verwechslung von oben,
  // nur umgekehrt.
  //
  // DERSELBE GRUND GILT BEI STUECKZAHL 1 (Antoine, 17.09.2026: „überprüfe alle Positionen, wo es
  // das gibt und wo es überflüssig ist"): „1× à 250,00 € · 250,00 €" nennt denselben Betrag
  // zweimal. Der Einzelpreis ist die Zusatzinformation zu einem MULTIPLIKATOR — gibt es keinen,
  // gibt es auch nichts zu ergaenzen. Ab „2×" steht er wieder da, weil dann zwei verschiedene
  // Zahlen gemeint sind.
  const einfach = absoluteStueckzahl(item, guests) === 1;
  return { formel: stueck ?? "Pauschal", betrag, einzelpreis: stueck == null || einfach ? null : money(item.unitPriceCents) };
}

/**
 * Migration 725 (Mockup freigegeben 15.10.2026): wie viele Gäste stehen noch ohne Paket da?
 * Nur sobald mindestens ein Paket-Kopf eine Teilgruppe trägt — sonst gilt jedes Paket für alle.
 * Je Tag gerechnet; gemeldet wird der größte Rest (ein Hinweis, nicht einer je Tag).
 */
export function gaesteOhnePaket(koepfe: OfferItem[], guestsForItem: (it: OfferItem) => number): { offen: number; alle: number } | null {
  if (!koepfe.some((k) => k.priceMode === "per_person" && k.guestCount != null)) return null;
  const jeTag = new Map<number | null, { gedeckt: number; alle: number }>();
  for (const k of koepfe) {
    if (k.priceMode !== "per_person") continue;
    const alle = guestsForItem(k);
    const t = jeTag.get(k.dayNo ?? null) ?? { gedeckt: 0, alle };
    t.gedeckt += k.guestCount != null && k.guestCount > 0 ? Math.min(k.guestCount, alle) : alle;
    jeTag.set(k.dayNo ?? null, t);
  }
  let best: { offen: number; alle: number } | null = null;
  for (const t of jeTag.values()) if (t.gedeckt < t.alle && (!best || t.alle - t.gedeckt > best.offen)) best = { offen: t.alle - t.gedeckt, alle: t.alle };
  return best;
}
